import { create } from "zustand";

/** Spec 031/US2 — estado del checkout drawer unificado (D8). */

export type CheckoutKind =
  | "addon"
  | "upgrade"
  | "annual"
  | "package"
  // Contratar un plan sin suscripción recurrente previa.
  | "subscribe"
  // Spec 036: pagar el propio período en modo manual (el plan que YA tiene).
  | "renewal"
  // Spec 044 (Fase B): pagar el estado de cuenta del mes (responsable mes a mes).
  | "statement";
export type CheckoutStep = "confirm" | "processing" | "success" | "error";
export type CheckoutMethod =
  | "saved"
  | "new_card"
  | "yape"
  // Spec 044 (US2): tarjeta nueva en un cobro que se suma al recurrente. Se
  // guarda sin volverse predeterminada y solo lo es si el cobro se aprueba.
  | "new_card_recurring";

export interface ICheckoutParams {
  /** kind=addon */
  type?: string;
  quantity?: number;
  /**
   * kind=addon, type='sede' (P1-3): nombre de la sede a crear. Cuando la sede
   * la cobra el estado de cuenta (`quote.siteViaStatement`) el drawer la crea
   * él mismo con POST /locals al confirmar.
   */
  siteName?: string;
  /** kind=upgrade | kind=subscribe */
  targetPlan?: string;
  /** kind=subscribe: "mensual" | "anual" */
  period?: string;
  /** kind=package */
  packageId?: number;
  /** kind=renewal: cuántos períodos pagar (por defecto, los pendientes). */
  periods?: number;
  /** kind=renewal (spec 044): adicionales que NO se renuevan en este pago. */
  excludeAddonIds?: number[];
  /** kind=statement (spec 044): meses a pagar por adelantado (1..12). */
  months?: number;
  /** kind=statement: claves de línea (`localId:concepto`) que NO se pagan. */
  excludeLineKeys?: string[];
  /** kind=statement: tarjeta guardada del responsable (de /billing/me). */
  savedCard?: { brand: string; lastFour: string; methodId?: number } | null;
}

/** Spec 044: modalidad del responsable de pago (motor propio). */
export type PayerMode = "automatic" | "monthly";

export type BlockedReason = "plan" | "sede_inactiva";

/** kind=renewal (spec 044): adicional de la renovación con su check. */
export interface IRenewalAddon {
  id: number;
  type: string;
  label: string;
  quantity: number;
  /** Bruto que suma a esta renovación; 0 si está excluido. */
  amount: number;
  included: boolean;
  localId?: number;
  localName?: string | null;
  /** Precio mensual NETO por unidad (se muestra con "+ IGV"). */
  unitNet?: number;
  entitlement?: boolean;
  /** Spec 044: motivo por el que no se puede renovar (línea no incluible). */
  blockedReason?: BlockedReason;
}

export interface IQuoteLine {
  label: string;
  /** Aclaración bajo la línea (precio mensual neto y fracción que se paga hoy). */
  detail?: string;
  amount: number;
  credit: boolean;
}

/** Cobro proporcional de hoy: qué parte del período cubre. */
export interface IQuoteProration {
  days: number;
  cycleDays: number;
  from: string;
  to: string | null;
  /** Lo que el adicional suma al total recurrente, sin IGV. */
  recurringNet: number;
}

/** Detalle del cambio de plan del motor propio (PlanChangeQuote de L2). */
export interface IPlanChangeQuoteDetail {
  mode: "first_contract" | "upgrade" | "downgrade" | "same_plan";
  modality: PayerMode;
  targetPlan: { key: string; name: string };
  /** Bajo el mínimo cobrable: lo de hoy se suma al mensual siguiente. */
  deferred?: boolean;
  /** Lo que cubre el cobro de hoy ('YYYY-MM-DD'). */
  coverage?: { from: string; to: string } | null;
  /** Primera contratación en prueba: último día de la prueba. */
  trialEndsAt?: string | null;
  /** Primer día de la cobertura pagada (día siguiente al fin de la prueba). */
  paidPlanStartsAt?: string | null;
  /** Bajar de plan: primer día con el plan nuevo. */
  effectiveAt?: string | null;
  allowedMethods?: ("yape" | "card" | "saved_card")[];
  message?: string;
  /**
   * Spec 044 (F-R2-11, aditivo pendiente en backend): el cambio no se puede
   * aplicar/programar (p. ej. FRANCHISES_ACTIVE en una baja de plan).
   */
  blocked?: { code: string; message?: string | null } | null;
}

export interface ICheckoutQuote {
  kind: CheckoutKind;
  title: string;
  lines: IQuoteLine[];
  today: { total: number; taxBase: number; igv: number };
  /** Presente cuando lo de hoy es una fracción del período (adicionales). */
  proration?: IQuoteProration | null;
  newRecurring: number | null;
  newRecurringPeriod: string | null;
  nextChargeDate: string | null;
  oneTime: boolean;
  note: string;
  /**
   * T3-A (FR-011): add-on cuyo proporcional queda bajo el mínimo cobrable.
   * Hoy no se cobra (se suma al próximo estado de cuenta): la compra se
   * confirma sin medio de pago.
   */
  deferred?: boolean;
  deferredTotal?: number;
  deferredNet?: number;
  /**
   * P1-3: sede adicional de una cadena con cierre de mes sin suscripción
   * nativa. Hoy no se cobra: el drawer confirma creando la sede (POST /locals
   * con acceptAddonCharge) y la cobra el estado de cuenta desde su alta.
   */
  siteViaStatement?: boolean;
  /**
   * P1-3: el mensual del mes siguiente ya estaba emitido (pagado por
   * adelantado o pendiente): ese mes de la sede nueva también se cobra en el
   * cierre `chargedOn` ("1 de diciembre").
   */
  siteExtraMonth?: { period: string; paid: boolean; label: string; net: number; chargedOn: string };
  recommendation: { show: boolean; text: string; targetPlan: string } | null;
  paymentMethods: {
    savedCard: { brand: string; lastFour: string; methodId?: number } | null;
    /**
     * Spec 044 (FR-021): modalidad del responsable. En el motor propio
     * decide los métodos: automático ⇒ tarjeta guardada + "Usar otra
     * tarjeta"; mes a mes ⇒ Yape + tarjeta sin guardar. Si no viene, el
     * drawer la lee de GET /account/billing/me.
     */
    payerMode?: PayerMode | null;
    yapeAvailable: boolean;
    /** Spec 032/D5: crédito Prikly disponible. */
    availableCredit: number;
    /** false cuando la pasarela no está configurada en el ambiente. */
    gatewayAvailable?: boolean;
  };
  /**
   * Spec 044 (L2): con el cierre de mes activo, `subscribe` y `upgrade` se
   * cotizan con el motor propio y la cotización trae este detalle. Si está
   * presente, el cobro va por POST /account/subscription/plan-change (o por
   * la baja programada) y NUNCA crea una suscripción de Culqi.
   */
  planChange?: IPlanChangeQuoteDetail | null;
  /** Solo kind=renewal (spec 044): adicionales con su check de inclusión. */
  renewalAddons?: IRenewalAddon[];
  /** Solo kind=statement (spec 044): filas por sede/concepto del estado de cuenta. */
  statement?: {
    months: number;
    rows: {
      key: string;
      siteName: string | null;
      label: string;
      excludable: boolean;
      subtotal: number;
    }[];
    paidThrough: string | null;
    graceUntil: string | null;
  };
}

/**
 * Lo que el drawer informa al caller al terminar. Aditivo: los callbacks que
 * no reciben argumentos siguen funcionando igual.
 */
export interface ICheckoutSuccessInfo {
  /** P1-3: el drawer ya creó la sede (no hay que volver a llamar POST /locals). */
  siteCreated?: {
    localId: number | null;
    name: string;
    /** P1-3: mes adicional de la sede que también va al cierre. */
    extraMonth?: { label: string; chargedOn: string } | null;
  };
}

export type CheckoutSuccessCallback = (info?: ICheckoutSuccessInfo) => void;

interface ICheckoutStore {
  open: boolean;
  kind: CheckoutKind | null;
  params: ICheckoutParams;
  quote: ICheckoutQuote | null;
  quoteLoading: boolean;
  quoteError: string | null;
  /** Código tipificado del error de cotización (ej. NO_ACTIVE_SUBSCRIPTION). */
  quoteErrorCode: string | null;
  step: CheckoutStep;
  method: CheckoutMethod;
  errorMessage: string | null;
  /** Callback del caller para refrescar datos tras una compra exitosa. */
  onSuccess: CheckoutSuccessCallback | null;

  openCheckout: (
    kind: CheckoutKind,
    params?: ICheckoutParams,
    onSuccess?: CheckoutSuccessCallback
  ) => void;
  closeCheckout: () => void;
  setParams: (params: ICheckoutParams) => void;
  setQuote: (quote: ICheckoutQuote | null) => void;
  setQuoteLoading: (loading: boolean) => void;
  setQuoteError: (error: string | null, code?: string | null) => void;
  setStep: (step: CheckoutStep) => void;
  setMethod: (method: CheckoutMethod) => void;
  setErrorMessage: (message: string | null) => void;
}

export const useCheckoutStore = create<ICheckoutStore>((set) => ({
  open: false,
  kind: null,
  params: {},
  quote: null,
  quoteLoading: false,
  quoteError: null,
  quoteErrorCode: null,
  step: "confirm",
  method: "saved",
  errorMessage: null,
  onSuccess: null,

  openCheckout: (kind, params = {}, onSuccess) =>
    set({
      open: true,
      kind,
      params,
      quote: null,
      quoteLoading: true,
      quoteError: null,
      quoteErrorCode: null,
      step: "confirm",
      // Se abre apostando por la tarjeta guardada (camino de un clic). Al
      // llegar la cotización, el drawer normaliza con `pickDefaultMethod`:
      // si el tipo de compra no la admite, cae al método que corresponda.
      method: "saved",
      errorMessage: null,
      onSuccess: onSuccess ?? null,
    }),
  closeCheckout: () =>
    set({
      open: false,
      kind: null,
      params: {},
      quote: null,
      quoteLoading: false,
      quoteError: null,
      quoteErrorCode: null,
      step: "confirm",
      // El método NO debe sobrevivir al cierre: arrastrarlo hacía que la
      // siguiente compra arrancara con el método de la anterior.
      method: "saved",
      errorMessage: null,
      onSuccess: null,
    }),
  setParams: (params) => set({ params }),
  setQuote: (quote) => set({ quote, quoteLoading: false }),
  setQuoteLoading: (quoteLoading) => set({ quoteLoading }),
  setQuoteError: (quoteError, quoteErrorCode = null) =>
    set({ quoteError, quoteErrorCode, quoteLoading: false }),
  setStep: (step) => set({ step }),
  setMethod: (method) => set({ method }),
  setErrorMessage: (errorMessage) => set({ errorMessage }),
}));
